import { Box, IconChevron, useResponsiveValue } from 'braid-design-system';
import React, { useState, type TouchEvent } from 'react';

import { useEventListener } from 'src/hooks/useEventListener';

import * as styles from './Carousel.css';

interface Props {
  children: JSX.Element[];
  show: {
    mobile: number;
    tablet: number;
    desktop: number;
    wide: number;
  };
  maxSlides?: number;
}

const Carousel = ({ children, show, maxSlides }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchPosition, setTouchPosition] = useState<number | null>(null);

  const responsiveValue = useResponsiveValue();
  const currentShow = responsiveValue(show) ?? show.desktop;

  const length = maxSlides ?? children.length;
  const atCarouselEnd = currentIndex >= length - currentShow;
  const atCarouselStart = currentIndex === 0;

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const touchDown = e.touches[0].clientX;
    setTouchPosition(touchDown);
  };

  const handleResize = () => {
    if (currentIndex + currentShow > length) {
      setCurrentIndex(currentIndex - (currentIndex + currentShow - length));
    } else if (currentShow === length) {
      setCurrentIndex(0);
    }
  };
  useEventListener('resize', handleResize);

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    const touchDown = touchPosition;

    if (touchDown === null) {
      return;
    }

    const currentTouch = e.touches[0].clientX;
    const diff = touchDown - currentTouch;

    if (diff > 5) {
      next();
    }

    if (diff < -5) {
      previous();
    }

    setTouchPosition(null);
  };

  const next = () => {
    if (!atCarouselEnd) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const previous = () => {
    if (!atCarouselStart) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.carouselWrapper}>
        {!atCarouselStart && (
          <Box
            className={styles.leftArrow}
            onClick={previous}
            data-testid="leftButton"
          >
            <IconChevron direction="left" />
          </Box>
        )}

        <div
          className={styles.carouselContentWrapper}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        >
          <div
            className={styles.carouselContent}
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
              width: `calc(100% / ${currentShow})`,
            }}
            data-testid="carouselContent"
          >
            {children.map((child, index) => (
              <Box display="flex" justifyContent="center" key={index}>
                {child}
              </Box>
            ))}
          </div>
        </div>
        {!atCarouselEnd && (
          <Box
            className={styles.rightArrow}
            onClick={next}
            data-testid="rightButton"
          >
            <IconChevron direction="right" />
          </Box>
        )}
      </div>
    </div>
  );
};

export default Carousel;
